// COP — HOST-OWNED imperative modal (NO React; cop.ts is the imperative shell).
// CONTRACT:C1-MAP-CLIENT.1.0
//
// A minimal promise-returning dialog the HOST summons for flows that must live
// outside the React tree (consent gates, composers) — the same "imperative shell"
// posture as the MapLibre controls. Used by:
//   - host/ownPosition.ts  — the TRUE-LOCATION double-consent dialog (OUR consent,
//     shown BEFORE the browser's geolocation prompt; both must consent).
//   - host/evacRequest.ts  — the CALL-FOR-EVAC composer (urgency + freetext).
//
// HEADLESS-SAFE: with no DOM (tsx checks / SSR) it resolves the dismiss value
// immediately — a consent dialog therefore FAILS CLOSED (dismiss = "no") when it
// cannot actually be shown. It never throws into the host.
//
// Styling is inline (dark tactical, matching the COP chrome) so this module owns
// no CSS file and cannot fight the component stylesheets.

/** One dialog button. `value` is what the returned promise resolves with. */
export interface HostDialogButton {
  label: string;
  value: string;
  /** Visual tone: "primary" (teal, the affirmative), "danger" (alert red — for
   *  send-EVAC-grade actions), "quiet" (neutral cancel). Default "quiet". */
  tone?: "primary" | "danger" | "quiet";
}

export interface HostDialogOptions {
  title: string;
  /** Build the body content into `body` with plain DOM. Closures may capture
   *  input elements and read them after the promise resolves. */
  build: (body: HTMLElement, doc: Document) => void;
  buttons: HostDialogButton[];
  /** Resolved on Esc / scrim click / headless (default null). A consent caller
   *  MUST map this to "not consented" — dismissal is never an accept. */
  dismissValue?: string | null;
  /** Injectable document (tests). Default: the global document when present. */
  doc?: Document;
}

const PANEL_BG = "#0d1420";
const PANEL_BORDER = "#2a3a4a";
const TEXT = "#cfd9e4";
const TEAL = "#3fb6a8";
const RED = "#e0564f";

function styleButton(btn: HTMLButtonElement, tone: HostDialogButton["tone"]): void {
  btn.style.cssText =
    "font: 600 12px/1 ui-monospace, Menlo, monospace; letter-spacing: 0.06em;" +
    "padding: 8px 14px; border-radius: 3px; cursor: pointer; text-transform: uppercase;";
  if (tone === "primary") {
    btn.style.background = TEAL;
    btn.style.border = `1px solid ${TEAL}`;
    btn.style.color = "#062221";
  } else if (tone === "danger") {
    btn.style.background = RED;
    btn.style.border = `1px solid ${RED}`;
    btn.style.color = "#1c0505";
  } else {
    btn.style.background = "transparent";
    btn.style.border = `1px solid ${PANEL_BORDER}`;
    btn.style.color = TEXT;
  }
}

/**
 * Show a host modal; resolve with the clicked button's `value`, or
 * `dismissValue` (default null) on Esc / scrim click / no-DOM.
 * Exactly one resolution; the overlay is removed before resolving.
 */
export function openHostDialog(opts: HostDialogOptions): Promise<string | null> {
  const dismiss = opts.dismissValue === undefined ? null : opts.dismissValue;
  const doc = opts.doc ?? (typeof document !== "undefined" ? document : undefined);
  // FAIL-CLOSED headless: no DOM ⇒ the dialog cannot be consented to ⇒ dismiss.
  if (!doc || !doc.body) return Promise.resolve(dismiss);

  return new Promise<string | null>((resolve) => {
    const overlay = doc.createElement("div");
    overlay.style.cssText =
      "position: fixed; inset: 0; z-index: 10000; display: flex; align-items: center;" +
      "justify-content: center; background: rgba(2, 6, 10, 0.72);";
    overlay.setAttribute("data-host-dialog", "");

    const panel = doc.createElement("div");
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-label", opts.title);
    panel.style.cssText =
      `min-width: 320px; max-width: 460px; background: ${PANEL_BG};` +
      `border: 1px solid ${PANEL_BORDER}; border-radius: 4px; padding: 16px 18px;` +
      `color: ${TEXT}; font: 13px/1.5 ui-monospace, Menlo, monospace;` +
      "box-shadow: 0 12px 40px rgba(0,0,0,0.6);";

    const titleEl = doc.createElement("div");
    titleEl.textContent = opts.title;
    titleEl.style.cssText =
      `font-weight: 700; font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase;` +
      `color: ${TEAL}; margin-bottom: 10px;`;
    panel.appendChild(titleEl);

    const body = doc.createElement("div");
    body.style.cssText = "margin-bottom: 14px;";
    opts.build(body, doc);
    panel.appendChild(body);

    const row = doc.createElement("div");
    row.style.cssText = "display: flex; gap: 8px; justify-content: flex-end;";

    let settled = false;
    const settle = (value: string | null): void => {
      if (settled) return;
      settled = true;
      doc.removeEventListener("keydown", onKey, true);
      overlay.remove();
      resolve(value);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.stopPropagation();
        settle(dismiss);
      }
    };

    for (const b of opts.buttons) {
      const btn = doc.createElement("button");
      btn.type = "button";
      btn.textContent = b.label;
      styleButton(btn, b.tone);
      btn.addEventListener("click", () => settle(b.value));
      row.appendChild(btn);
    }
    panel.appendChild(row);

    // Scrim click = dismiss (never an accept); clicks inside the panel don't bubble out.
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) settle(dismiss);
    });
    panel.addEventListener("click", (e) => e.stopPropagation());

    doc.addEventListener("keydown", onKey, true);
    overlay.appendChild(panel);
    doc.body.appendChild(overlay);

    // Focus the LAST button (by convention the affirmative sits last) so keyboard
    // users see focus land in the dialog; Esc always dismisses.
    const last = row.lastElementChild as HTMLButtonElement | null;
    try {
      last?.focus();
    } catch {
      /* focus can throw on detached/headless-ish docs — non-fatal */
    }
  });
}
